"use client"

import { formatDate } from "@abraxas/ui/lib"
import { useSales } from "./hooks/useSales"
import { formatEther } from "viem"
import { useMemo } from "react"

interface TokenSalesProps {
  listingIds: number[]
}

export const TokenSales: React.FC<TokenSalesProps> = ({ listingIds }) => {
  const { data: allSales, isLoading, error } = useSales()

  const filteredSales = useMemo(() => {
    if (!allSales) {
      return []
    }
    return allSales.filter((sale) => listingIds.includes(sale.listingId))
  }, [allSales, listingIds])

  if (isLoading) {
    return (
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Sales History</h3>
        <div className="text-gray-500">Loading sales history...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Sales History</h3>
        <div className="text-red-500">Error loading sales history</div>
      </div>
    )
  }

  if (filteredSales.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Sales History</h3>
        <div className="text-gray-500">No sales history available</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Sales History</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
              <th className="px-4 py-2 text-left text-sm font-medium">
                Total Price
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium">
                Price/Token
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium">
                Quantity
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium">Buyer</th>
            </tr>
          </thead>
          <tbody>
            {filteredSales.map((sale) => (
              <tr key={sale.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">
                  {formatDate(new Date(Number(sale.createdAt) * 1000))}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className="text-green-600 font-semibold">
                    {formatEther(BigInt(sale.totalPricePaid))} ETH
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {formatEther(BigInt(sale.pricePerToken))} ETH
                </td>
                <td className="px-4 py-3 text-sm">{sale.quantityBought}</td>
                <td className="px-4 py-3 text-sm font-mono">
                  {sale.buyer.slice(0, 6)}...{sale.buyer.slice(-4)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
